/**
 * "Provably fair" — what a player can check (the rarity roll), how it is
 * switched on, and the exact protocol (pf-a-v1) so anyone can verify a receipt
 * without trusting our code. Partner-facing markdown. The endpoints live under
 * Player API → Provably Fair.
 *
 * The verifier below is the reference one: it was run against receipts built by
 * the backend (and against a tampered copy) before it went into the docs.
 */

// String.raw keeps the snippet byte-exact; it must not contain a backtick or "${".
const VERIFIER = String.raw`// pf-a-v1 receipt verifier: WebCrypto only (any browser, Node 20+).
const enc = new TextEncoder();
const hex = (h) => Uint8Array.from(h.match(/../g), (b) => parseInt(b, 16));
const toHex = (b) => [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
const cat = (...parts) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let i = 0;
  for (const p of parts) { out.set(p, i); i += p.length; }
  return out;
};
const u64be = (n) => { const b = new Uint8Array(8); new DataView(b.buffer).setBigUint64(0, BigInt(n)); return b; };
const field = (b) => { const len = new Uint8Array(4); new DataView(len.buffer).setUint32(0, b.length); return cat(len, b); };
const str = (s) => field(enc.encode(s));
const u64 = (n) => field(u64be(n));
const sha256 = async (b) => new Uint8Array(await crypto.subtle.digest('SHA-256', b));

async function oddsDigest(odds) {
  const parts = [str('pf-a-v1/odds')];
  for (const level of ['normal', 'high', 'max']) {
    if (!odds[level]) continue;
    parts.push(str(level), u64(odds[level].length));
    for (const e of odds[level]) parts.push(str(e.tier), u64(e.ticks));
  }
  return sha256(cat(...parts));
}

async function roll(serverSeed, r, digest, slot) {
  const msg = cat(str('pf-a-v1'), str(r.client_seed), str(r.ticket.id), field(digest),
    field(hex(r.ticket.params_fingerprint)), str(r.volatility), u64(r.quantity), u64(slot));
  const key = await crypto.subtle.importKey('raw', serverSeed, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const range = 100000000n;
  const limit = ((1n << 64n) / range) * range;
  for (let counter = 0n; ; counter++) {
    const block = new DataView(await crypto.subtle.sign('HMAC', key, cat(msg, u64be(counter))));
    for (let i = 0; i < 32; i += 8) {
      const value = block.getBigUint64(i);
      if (value < limit) return Number(value % range);
    }
  }
}

function tierFor(entries, value) {
  let total = 0;
  for (const e of entries) { total += e.ticks; if (value < total) return e.tier; }
}

async function signatureOk(t, publicKeyPem) {
  const der = Uint8Array.from(atob(publicKeyPem.replace(/-----[^-]+-----|\s/g, '')), (c) => c.charCodeAt(0));
  const key = await crypto.subtle.importKey('spki', der, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  const msg = cat(str('pf-a-v1/ticket'), str(t.id), u64(t.pool_offer_id), field(hex(t.server_seed_hash)),
    field(hex(t.odds_digest)), field(hex(t.params_fingerprint)), u64(t.max_quantity),
    u64(t.issued_at_ms), u64(t.valid_until_ms));
  return crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, hex(t.signature), msg);
}

// r: a receipt; publicKeyPem: the key from GET /fairness/keys whose key_id is r.ticket.signing_key_id.
async function verify(r, publicKeyPem) {
  const t = r.ticket;
  const seed = hex(r.server_seed);
  if (toHex(await sha256(seed)) !== t.server_seed_hash) return 'server seed does not match its hash';
  const digest = await oddsDigest(t.odds);
  if (toHex(digest) !== t.odds_digest) return 'odds differ from the signed ticket';
  if (!(await signatureOk(t, publicKeyPem))) return 'ticket is not signed by Repackz';
  for (const s of r.slots.filter((x) => x.provably_fair)) {
    const value = await roll(seed, r, digest, s.slot);
    if (value !== s.roll || tierFor(t.odds[r.volatility], value) !== s.tier) return 'slot ' + s.slot + ' does not match';
  }
  return 'verified';
}`;

export const provablyFair: string[] = [
  '## Provably fair',
  '',
  '**Every rarity roll is provably fair.** Before a purchase we fix the odds and show the hash of a secret',
  'server seed, both signed by us; the player’s device adds its own random seed; together they decide each',
  'card’s rarity tier. After the purchase we reveal the server seed, so anyone can recompute every roll and',
  'check it against the odds fixed in advance. It is the commit–reveal method used across provably-fair games:',
  'neither side can steer the roll, and we cannot change it after the fact.',
  '',
  '### What the player sees (hosted player experience)',
  '',
  '- **Odds:** “Provably Fair · Learn More” at the foot of the odds — how it works, the hash of the server seed',
  '  for the next purchase and the player’s own seed.',
  '- **My Packs:** “Verify the Rarity Roll” on every card from a provably-fair purchase opens **Verify**. It',
  '  recomputes every roll in the browser from the receipt and our published key, and shows the verdict, each',
  '  card’s roll and tier, and both seeds. A link opens the same check on **verify.repackz.fun**, our public',
  '  checker, and the receipt can be copied to check it anywhere else.',
  '',
  '### What is proven — and what is not',
  '',
  '- The odds and the hash of our seed were fixed **before** the purchase and signed by us.',
  '- The seed we reveal matches that hash, so it was not chosen after seeing the player’s seed.',
  '- The roll recomputed from both seeds lands in the rarity tier the player received.',
  '',
  'Which card is drawn **inside** the rolled tier is covered by our audited outcome log, not by the',
  'player-side proof. Jackpot cards you award and promotional awards involve no roll; their slots are marked',
  '`provably_fair: false`. If the rolled tier sells out in the meantime, the whole purchase is refunded',
  '(`TIER_EXHAUSTED`): a roll is never repeated and never moved to another tier.',
  '',
  '### Switching it on',
  '',
  'Provably fair is **off by default**. We switch it on for your platform on request, in one of two modes:',
  '',
  '| Mode | Effect |',
  '| --- | --- |',
  '| **Optional** | A purchase that carries a ticket is provably fair; one without works as today. The hosted player experience always sends a ticket and does not buy without one. |',
  '| **Required** | A purchase without a ticket is rejected before any charge (`FAIRNESS_REQUIRED`). |',
  '',
  'The mode covers all your packs. On the hosted player experience there is nothing to build: the iframe',
  'reads `provably_fair: true` from **Get the resolved theme**, requests tickets, generates the player seed',
  'and shows Verify. A purchase is never downgraded silently — a ticket sent while provably fair is off is',
  'rejected before any charge. Promotion players are never provably fair.',
  '',
  '### Flow for your own UI',
  '',
  '1. **Get a ticket** for the pack the player is looking at; show `server_seed_hash`. A ticket is valid for',
  '   about 90 seconds and buys once — request the next one in time.',
  '2. Generate a fresh **player seed**: 32 random bytes as 64 hex characters.',
  '3. Buy with `fairness: { ticket_id, client_seed }` (**Buy a pack (one call)**). A `FAIRNESS_TICKET_*` or',
  '   `FAIRNESS_PARAMS_MISMATCH` refusal charges nothing — get a new ticket and send again.',
  '4. The response carries `pool_order.fairness` with both seeds and each slot’s roll. Show Verify with it, or',
  '   later with **Get a receipt** (the `fairness_ticket_id` on inventory cards points to it).',
  '',
  'Why a ticket per purchase instead of one seed pair per session: our odds move with the pack’s stock.',
  'Fixing the odds together with our seed hash, before the player’s seed is known, means we can never pick',
  'the odds after seeing a roll.',
  '',
  '### The algorithm (protocol `pf-a-v1`)',
  '',
  '- **Encoding.** Every hashed, signed or HMAC’d message is a concatenation of fields; each field is a',
  '  4-byte big-endian length followed by the bytes. `str(x)` is the UTF-8 text, `u64(n)` an 8-byte big-endian',
  '  unsigned integer, `bytes(x)` raw bytes (hex values from the receipt are decoded first).',
  '- **Server seed.** 32 random bytes; `server_seed_hash = hex(SHA-256(server_seed))`.',
  '- **Odds digest.** `SHA-256( str("pf-a-v1/odds") ‖ for each level present, in the order normal, high, max:',
  '  str(level) ‖ u64(number of tiers) ‖ for each tier in ticket order: str(tier) ‖ u64(ticks) )`.',
  '- **Ticket signature.** ECDSA P-256 with SHA-256 over `str("pf-a-v1/ticket") ‖ str(id) ‖ u64(pool_offer_id) ‖',
  '  bytes(server_seed_hash) ‖ bytes(odds_digest) ‖ bytes(params_fingerprint) ‖ u64(max_quantity) ‖',
  '  u64(issued_at_ms) ‖ u64(valid_until_ms)`; `signature` is raw `r ‖ s` hex. `params_fingerprint` commits',
  '  to the pack price and settings; verifiers use it as given.',
  '- **Roll message.** `str("pf-a-v1") ‖ str(client_seed) ‖ str(ticket id) ‖ bytes(odds_digest) ‖',
  '  bytes(params_fingerprint) ‖ str(volatility) ‖ u64(quantity) ‖ u64(slot)` — `client_seed` exactly as',
  '  sent (64 hex characters as text), `slot` 0-based.',
  '- **Random stream.** Block `c` = `HMAC-SHA256(key = server_seed, roll message ‖ 8-byte big-endian c)`',
  '  for `c = 0, 1, …` (the counter is appended raw, not as a field), read as four big-endian 64-bit numbers.',
  '- **Roll.** The first number below `floor(2^64 / 100 000 000) × 100 000 000`, modulo 100 000 000 —',
  '  rejection sampling, no modulo bias. Each slot gets exactly one roll.',
  '- **Tier.** The `odds` list of the purchase’s volatility, in ticket order (lowest value tier first,',
  '  ticks summing to 100 000 000): the first tier whose running total of ticks exceeds the roll.',
  '',
  'Anyone can check a receipt at `https://verify.repackz.fun/?ticket=<ticket id>` — the page runs this exact',
  'check in the browser, sets no cookies and talks only to our API. To check it yourself:',
  '',
  'Verifying a receipt: the server seed matches its hash, the odds match `odds_digest`, the ticket is',
  'signed by the key **Get the signing keys** publishes under `signing_key_id`, and every slot with',
  '`provably_fair: true` recomputes to its `roll` and `tier`.',
  '',
  '```js',
  ...VERIFIER.split('\n'),
  '```',
  '',
  '**Test vector.** `server_seed` = `7f` × 32 bytes (hash `17a7384bf1c50a94b712ce507c5ccd58638cd091278078c6e7dc00ae0aa152fc`),',
  '`client_seed` = `"3c" × 32`, ticket id = `"9" × 32`, `params_fingerprint` =',
  '`576e3d9124ef023f41e9766aed9a36803d1e4eb5b4148e09bb9d0909cdfedbe2`, odds',
  '`normal: 0..50 → 78500000, 50..100 → 18000000, 100..Infinity → 3500000` and',
  '`max: 0..50 → 60000000, 50..100 → 0, 100..Infinity → 40000000` (digest',
  '`5488b1de335aba64340540464847fbd8213fca25029951dff64fe9cca9ddb10f`), volatility `max`, quantity 2:',
  'slot 0 rolls **31 068 478** and slot 1 rolls **20 637 331**, both tier `0..50`.',
  '',
];
